import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, Alert, Divider } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { entityPhotoHint, validateEntityPhotoFile } from '../../utils/uploadLimits';
import { EntityImageEditor } from '../entityImage/EntityImageEditor';
import { getEntityImageConfig } from '../entityImage/entityImageConfig';
import { uploadEntityImage } from '../entityImage/entityImageApi';
import { useState, useCallback, useEffect } from 'react';
import VideoFileDropzone from '../video/VideoFileDropzone';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import { cleanRichText } from '../../utils/richText';
import RichTextEditor from '../richText/RichTextEditor';
import useContentFormDialog from '../richText/useContentFormDialog';
import '../../css/users/addUserDialog.css';

const ARTICLE_FOLDER_IMAGE_CONFIG = getEntityImageConfig('articleFolder');

export const AddArticleFolderDialog = ({ setArticleFolders, setTotalArticleFolders, setFilteredArticleFoldersCount, openAddArticleFolder, setOpenAddArticleFolder, setOpenSuccessfulAddArticleFolder }) => {

    const { t } = useTranslation();

    const [errMsg, setErrMsg] = useState(null);
    /** Source being cropped in the unified editor (create flow: nothing is uploaded yet). */
    const [editorSource, setEditorSource] = useState(null);
    const [openEditor, setOpenEditor] = useState(false);
    /** Crop prepared in memory, posted to `article-folder-image` once the folder exists. */
    const [preparedImage, setPreparedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
    }, [previewUrl]);

    useEffect(() => () => {
        if (editorSource?.src) {
            URL.revokeObjectURL(editorSource.src);
        }
    }, [editorSource]);

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
        },
        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            description: Yup.string().optional(),
        }),
        onSubmit: async (values) => {
            try {
                const formData = new FormData();
                formData.append('title', values.title || '');
                formData.append('description', cleanRichText(values.description));

                const response = await axios.post('article-folder', formData);
                if (response.status !== 200 && response.status !== 201) {
                    return;
                }

                let created = response.data;
                if (preparedImage && created?.id) {
                    const imageResponse = await uploadEntityImage(
                        ARTICLE_FOLDER_IMAGE_CONFIG,
                        created.id,
                        preparedImage,
                    );
                    if (imageResponse?.data) {
                        created = { ...created, article_folder_image: [imageResponse.data] };
                    }
                }

                setArticleFolders(prevArticleFolders => [...prevArticleFolders, created]);
                setTotalArticleFolders((prevTotal) => prevTotal + 1);
                setFilteredArticleFoldersCount((prevCount) => prevCount + 1);
                setOpenAddArticleFolder(false);
                setErrMsg(null);
                setOpenSuccessfulAddArticleFolder(true);
            } catch (error) {
                setErrMsg(error.message || 'Erreur');
                CustomErrorLogger(error, 'Erreur lors de l\'ajout du dossier');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, isSubmitting, setFieldValue } = formik;
    const dialogProps = useContentFormDialog();

    const handleFileChange = useCallback((files) => {
        setErrMsg(null);

        if (!files?.length) {
            return;
        }
        if (files.length > 1) {
            setErrMsg(t('Une seule image est autorisée.'));
            return;
        }

        const validation = validateEntityPhotoFile(files[0], t);
        if (!validation.ok) {
            setErrMsg(validation.message);
            return;
        }

        setEditorSource({ file: files[0], src: URL.createObjectURL(files[0]) });
        setOpenEditor(true);
    }, [t]);

    const handleLocalConfirm = useCallback((prepared) => {
        setPreparedImage(prepared);
        setPreviewUrl((previous) => {
            if (previous) {
                URL.revokeObjectURL(previous);
            }
            return URL.createObjectURL(prepared.croppedFile);
        });
    }, []);

    const handleClose = useCallback(() => {
        setOpenAddArticleFolder(false);
        setErrMsg(null);
    }, [setOpenAddArticleFolder]);

    return (
        <Dialog open={openAddArticleFolder} {...dialogProps}>
            <IconButton onClick={handleClose} className='addUser_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addUser_close_icon' />
            </IconButton>
            <DialogTitle className='addUser_dialog_title'>{t('addArticleFolderDialogTitle')}</DialogTitle>
            <Divider />
            {errMsg && <Alert key={errMsg} severity="error">{errMsg}</Alert>}
            <form className='contentFormDialog_form' onSubmit={handleSubmit}>
                <DialogContent className='addUser_dialog_content contentFormDialog_content'>
                    <Typography className='addUser_dialog_SubTitle'>{t('addArticleFolderDialogSubTitle')}</Typography>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t('Titre')}</FormHelperText>
                        <TextField
                            fullWidth
                            value={values.title}
                            className='addUser_InputField'
                            id="outlined-adornment-weight"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="title"
                            error={touched.title && Boolean(errors.title)}
                            helperText={touched.title && errors.title}
                            aria-describedby="outlined-weight-helper-text"
                        />
                    </Box>
                    <Box className="addUser_helper_selct_box contentFormDialog_description">
                        <FormHelperText className="addUserInputTitle">{t('Description')} (optionnel)</FormHelperText>
                        <RichTextEditor
                            value={values.description}
                            onChange={(html) => setFieldValue('description', html)}
                            placeholder={t('Description')}
                        />
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            borderRadius: '8px',
                            textAlign: 'center',
                            position: 'relative',
                        }}
                        onDragOver={(event) => {
                            event.preventDefault();
                            event.currentTarget.style.borderColor = '#007bff';
                        }}
                        onDragLeave={(event) => {
                            event.currentTarget.style.borderColor = '#ccc';
                        }}
                        onDrop={(event) => {
                            event.preventDefault();
                            event.currentTarget.style.borderColor = '#ccc';
                            handleFileChange(event.dataTransfer?.files);
                        }}
                    >
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">{t("L'image de dossier (optionnel)")}</FormHelperText>
                        <VideoFileDropzone
                            accept="image/png,image/jpeg,image/webp"
                            hint={entityPhotoHint(t)}
                            onFiles={handleFileChange}
                        />
                    </Box>
                    {previewUrl && (
                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                            <Box
                                component="img"
                                src={previewUrl}
                                alt={t('Aperçu de la couverture')}
                                sx={{ maxWidth: '100%', maxHeight: 220, borderRadius: '8px' }}
                            />
                            <Button size="small" onClick={() => setOpenEditor(true)} sx={{ textTransform: 'none' }}>
                                {t('Recadrer')}
                            </Button>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className='addUser_dialog_actions'>
                    <Button className='addUser_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addUser_button_valider' variant="contained" type="submit" disabled={isSubmitting}>{t('Valider')}</Button>
                </DialogActions>
            </form>

            {openEditor && (
                <EntityImageEditor
                    entityType="articleFolder"
                    mode="local"
                    initialFile={editorSource?.file}
                    initialImageSrc={editorSource?.src}
                    modifyPhoto={openEditor}
                    setModifyPhoto={setOpenEditor}
                    onLocalConfirm={handleLocalConfirm}
                    dialogTitle={t('Préparer l’image du dossier')}
                />
            )}
        </Dialog>
    );
};
